/**
 * Column Statistics Component
 * Displays descriptive statistics for a data column
 */
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ColumnStatistics as ColumnStatsType } from '../../services/columnStatisticsService';

interface ColumnStatisticsProps {
  statistics: ColumnStatsType | null;
  loading: boolean;
}

const COLORS = ['#003366', '#0066CC', '#4A90E2', '#7CB9E8', '#B0D4F1', '#E3F2FD'];

const ColumnStatistics: React.FC<ColumnStatisticsProps> = ({ statistics, loading }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Loading statistics...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!statistics) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No statistical data available for this column.
        </Typography>
      </Box>
    );
  }

  const isNumeric = statistics.mean !== undefined;
  const isText = statistics.avgLength !== undefined;
  const isDate = statistics.minDate !== undefined;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Descriptive Statistics
        </Typography>
        <Chip 
          label="Sample Data" 
          size="small" 
          sx={{ 
            bgcolor: '#E3F2FD', 
            color: '#1976d2',
            fontWeight: 500,
            fontSize: '0.75rem'
          }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
        Based on typical data patterns for this column type. Actual statistics would be calculated from real data profiling.
      </Typography>

      {/* Data Quality Metrics */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Data Quality
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Total Records</Typography>
            <Typography variant="h6">{statistics.totalRecords.toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Populated</Typography>
            <Typography variant="h6">{statistics.populatedRecords.toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Null/Empty</Typography>
            <Typography variant="h6">{statistics.nullRecords.toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Completeness</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{statistics.completeness}%</Typography>
              <Chip 
                label={statistics.completeness >= 95 ? 'Excellent' : statistics.completeness >= 80 ? 'Good' : 'Needs Improvement'}
                size="small"
                color={statistics.completeness >= 95 ? 'success' : statistics.completeness >= 80 ? 'primary' : 'warning'}
              />
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={statistics.completeness} 
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>
      </Paper>

      {/* Numeric Statistics */}
      {isNumeric && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Numeric Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Min</Typography>
              <Typography variant="body1">{statistics.min?.toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Max</Typography>
              <Typography variant="body1">{statistics.max?.toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Mean</Typography>
              <Typography variant="body1">{statistics.mean?.toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Median</Typography>
              <Typography variant="body1">{statistics.median?.toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Mode</Typography>
              <Typography variant="body1">{statistics.mode?.toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Std Dev</Typography>
              <Typography variant="body1">{statistics.stdDev?.toLocaleString()}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Text Statistics */}
      {isText && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Text Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Unique Values</Typography>
              <Typography variant="body1">{statistics.uniqueValues?.toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Avg Length</Typography>
              <Typography variant="body1">{statistics.avgLength} chars</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Min Length</Typography>
              <Typography variant="body1">{statistics.minLength} chars</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Max Length</Typography>
              <Typography variant="body1">{statistics.maxLength} chars</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Top Values */}
      {statistics.topValues && statistics.topValues.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Most Common Values (Top 10)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statistics.topValues}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="value" 
                angle={-45} 
                textAnchor="end" 
                height={100}
                interval={0}
                style={{ fontSize: '12px' }}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#003366" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Distribution - Categories */}
      {statistics.distribution?.categories && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Industry Distribution
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statistics.distribution.categories}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percentage }) => `${name}: ${percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="percentage"
              >
                {statistics.distribution.categories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Distribution - Ranges */}
      {statistics.distribution?.ranges && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Value Distribution
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statistics.distribution.ranges}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#0066CC" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

export default ColumnStatistics;
